import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Button from '../components/ui/button';
import AnimatedBackground from '../components/ui/animated-background';
import { submitWaitlistForm, WaitlistFormData as WaitlistData } from '../services/waitlist-service';

// Step interface for multi-step form
interface FormStep {
  id: number;
  title: string;
}

// Form data interface
interface WaitlistFormData {
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  companySize: string;
  taxSoftware: string[];
  numberOfReturns: string;
  cardNumber: string;
  cardExpiry: string;
  cardCvc: string;
  cardName: string;
}

const WaitlistContainer = styled.div`
  padding: 6rem 1rem 6rem;
  max-width: 800px;
  margin: 0 auto;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    width: 600px;
    height: 600px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(124, 58, 237, 0.06) 0%, rgba(124, 58, 237, 0) 70%);
    top: -200px;
    right: -200px;
    z-index: -1;
    animation: float 20s infinite alternate ease-in-out;
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 500px;
    height: 500px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(236, 72, 153, 0.05) 0%, rgba(236, 72, 153, 0) 70%);
    bottom: -150px;
    left: -150px;
    z-index: -1;
    animation: float 15s infinite alternate-reverse ease-in-out;
  }
  
  @keyframes float {
    0% {
      transform: translate(0, 0) scale(1);
    }
    50% {
      transform: translate(30px, -30px) scale(1.05);
    }
    100% {
      transform: translate(0, 0) scale(1);
    }
  }
`;

const PageTitle = styled(motion.h1)`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  text-align: center;
  background: ${({ theme }) => theme.colors.gradient.text};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: -0.02em;
  position: relative;
  z-index: 1;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2.5rem;
  }
`;

const PageSubtitle = styled(motion.p)`
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.gray};
  margin-bottom: 3rem;
  text-align: center;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  position: relative;
  z-index: 1;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.125rem;
  }
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 3rem;
  position: relative;
  z-index: 2;
`;

const Step = styled.div<{ active: boolean; completed: boolean }>`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 0.5rem;
  background: ${({ active, completed, theme }) => 
    completed ? theme.colors.gradient.cta : 
    active ? 'white' : 'rgba(255, 255, 255, 0.7)'};
  color: ${({ active, completed, theme }) => 
    completed ? 'white' : 
    active ? theme.colors.primary : theme.colors.gray};
  border: ${({ active, completed, theme }) => 
    completed ? 'none' : 
    active ? `2px solid ${theme.colors.primary}` : '2px solid rgba(124, 58, 237, 0.2)'};
  font-weight: 700;
  transition: ${({ theme }) => theme.transitions.default};
  box-shadow: ${({ active, completed, theme }) => 
    completed ? theme.shadows.glow : 
    active ? '0 4px 12px rgba(124, 58, 237, 0.15)' : 'none'};
  transform: ${({ active }) => active ? 'scale(1.05)' : 'scale(1)'};
`;

const StepConnector = styled.div<{ active: boolean }>`
  height: 3px;
  width: 70px;
  background: ${({ active, theme }) => active ? theme.colors.gradient.cta : 'rgba(124, 58, 237, 0.15)'};
  align-self: center;
  transition: ${({ theme }) => theme.transitions.default};
  border-radius: ${({ theme }) => theme.radii.full};
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${({ active }) => active ? '100%' : '0'};
    background: ${({ theme }) => theme.colors.gradient.cta};
    transition: width 0.5s ease;
  }
`;

const FormContainer = styled(motion.div)`
  background-color: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: ${({ theme }) => theme.radii.xl};
  padding: 2.5rem;
  box-shadow: ${({ theme }) => theme.shadows.card};
  border: 1px solid rgba(255, 255, 255, 0.8);
  position: relative;
  overflow: hidden;
  z-index: 1;
  
  &::before {
    content: '';
    position: absolute;
    top: -50px;
    right: -50px;
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.gradient.cta};
    opacity: 0.1;
    filter: blur(20px);
    z-index: -1;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -30px;
    left: -30px;
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.gradient.secondary};
    opacity: 0.1;
    filter: blur(15px);
    z-index: -1;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid ${({ theme }) => theme.colors.grayLight};
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(30, 86, 49, 0.2);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid ${({ theme }) => theme.colors.grayLight};
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(30, 86, 49, 0.2);
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-right: 1rem;
  margin-bottom: 0.5rem;
`;

const Checkbox = styled.input`
  margin-right: 0.5rem;
  cursor: pointer;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
`;

const ErrorMessage = styled.p`
  color: ${({ theme }) => theme.colors.danger};
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const SuccessContainer = styled(motion.div)`
  text-align: center;
  padding: 3rem 2rem;
`;

const SuccessIcon = styled.div`
  font-size: 4rem;
  color: ${({ theme }) => theme.colors.success};
  margin-bottom: 1.5rem;
`;

const SuccessTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const SuccessMessage = styled.p`
  font-size: 1.125rem;
  color: ${({ theme }) => theme.colors.gray};
  margin-bottom: 2rem;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
`;

const CardRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const PricingInfo = styled.div`
  background-color: ${({ theme }) => theme.colors.light};
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const PriceTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
`;

const Price = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const PriceDescription = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.gray};
`;

const WaitlistPage: React.FC = () => {
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submissionError, setSubmissionError] = useState<string>('');
  const [formData, setFormData] = useState<WaitlistFormData>({
    firstName: '',
    lastName: '',
    email: '',
    companyName: '',
    companySize: '',
    taxSoftware: [],
    numberOfReturns: '',
    cardNumber: '',
    cardExpiry: '',
    cardCvc: '',
    cardName: '',
  });

  useEffect(() => {
    document.title = 'Join Waitlist | FylePrep';
    window.scrollTo(0, 0);
  }, []);

  const steps: FormStep[] = [
    { id: 1, title: 'Contact Information' },
    { id: 2, title: 'Business Details' },
    { id: 3, title: 'Payment Information (Optional)' },
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear error when user types
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value, checked } = e.target;
    let updatedSoftware = [...formData.taxSoftware];
    
    if (checked) {
      updatedSoftware.push(value);
    } else {
      updatedSoftware = updatedSoftware.filter(item => item !== value);
    }
    
    setFormData({ ...formData, taxSoftware: updatedSoftware });
    
    // Clear error when user selects
    if (errors.taxSoftware) {
      setErrors({ ...errors, taxSoftware: '' });
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (step === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email is invalid';
      }
    } else if (step === 2) {
      if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
      if (!formData.companySize) newErrors.companySize = 'Company size is required';
      if (formData.taxSoftware.length === 0) newErrors.taxSoftware = 'Please select at least one tax software';
      if (!formData.numberOfReturns) newErrors.numberOfReturns = 'Number of returns is required';
    } else if (step === 3) {
      // Payment information is optional - only validate if data was entered
      if (formData.cardName.trim() || formData.cardNumber.trim() || formData.cardExpiry.trim() || formData.cardCvc.trim()) {
        if (!formData.cardName.trim()) newErrors.cardName = 'Name on card is required';
        if (!formData.cardNumber.trim()) {
          newErrors.cardNumber = 'Card number is required';
        } else if (!/^\d{16}$/.test(formData.cardNumber.replace(/\s/g, ''))) {
          newErrors.cardNumber = 'Card number must be 16 digits';
        }
        
        if (!formData.cardExpiry.trim()) {
          newErrors.cardExpiry = 'Expiry date is required';
        } else if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(formData.cardExpiry)) {
          newErrors.cardExpiry = 'Expiry date must be in MM/YY format';
        }
        
        if (!formData.cardCvc.trim()) {
          newErrors.cardCvc = 'CVC is required';
        } else if (!/^\d{3,4}$/.test(formData.cardCvc)) {
          newErrors.cardCvc = 'CVC must be 3 or 4 digits';
        }
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = (): void => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevious = (): void => {
    setCurrentStep(currentStep - 1);
    window.scrollTo(0, 0);
  };

  const handleSkipPayment = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmissionError('');
    
    try {
      // Submit to Firebase without payment info
      await submitWaitlistForm({
        ...formData,
        cardNumber: '',
        cardExpiry: '',
        cardCvc: '',
        cardName: '',
        hasPaymentInfo: false
      });
      
      setIsSubmitted(true);
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmissionError('There was an error submitting your information. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    
    if (validateStep(currentStep)) {
      setIsSubmitting(true);
      setSubmissionError('');
      
      try {
        // Check if any payment info was entered
        const hasPaymentInfo = !!(formData.cardNumber || formData.cardExpiry || formData.cardCvc || formData.cardName);
        
        // Submit to Firebase
        await submitWaitlistForm({
          ...formData,
          hasPaymentInfo
        });
        
        setIsSubmitted(true);
        window.scrollTo(0, 0);
      } catch (error) {
        console.error('Error submitting form:', error);
        setSubmissionError('There was an error submitting your information. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const renderStepIndicator = () => {
    return (
      <StepIndicator>
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            <Step 
              active={currentStep === step.id} 
              completed={currentStep > step.id}
            >
              {currentStep > step.id ? '✓' : step.id}
            </Step>
            {index < steps.length - 1 && (
              <StepConnector active={currentStep > step.id} />
            )}
          </React.Fragment>
        ))}
      </StepIndicator>
    );
  };

  const renderContactForm = () => {
    return (
      <>
        <FormGroup>
          <Label htmlFor="firstName">First Name</Label>
          <Input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
          />
          {errors.firstName && <ErrorMessage>{errors.firstName}</ErrorMessage>}
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="lastName">Last Name</Label>
          <Input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
          />
          {errors.lastName && <ErrorMessage>{errors.lastName}</ErrorMessage>}
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="email">Email Address</Label>
          <Input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
          />
          {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
        </FormGroup>
      </>
    );
  };

  const renderBusinessForm = () => {
    return (
      <>
        <FormGroup>
          <Label htmlFor="companyName">Company Name</Label>
          <Input
            type="text"
            id="companyName"
            name="companyName"
            value={formData.companyName}
            onChange={handleInputChange}
          />
          {errors.companyName && <ErrorMessage>{errors.companyName}</ErrorMessage>}
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="companySize">Company Size</Label>
          <Select
            id="companySize"
            name="companySize"
            value={formData.companySize}
            onChange={handleInputChange}
          >
            <option value="">Select company size</option>
            <option value="1">Solo practitioner</option>
            <option value="2-5">2-5 employees</option>
            <option value="6-10">6-10 employees</option>
            <option value="11-25">11-25 employees</option>
            <option value="26-50">26-50 employees</option>
            <option value="51+">51+ employees</option>
          </Select>
          {errors.companySize && <ErrorMessage>{errors.companySize}</ErrorMessage>}
        </FormGroup>
        
        <FormGroup>
          <Label>Tax Software Used</Label>
          <CheckboxGroup>
            <CheckboxLabel>
              <Checkbox
                type="checkbox"
                name="taxSoftware"
                value="UltraTax"
                checked={formData.taxSoftware.includes('UltraTax')}
                onChange={handleCheckboxChange}
              />
              UltraTax
            </CheckboxLabel>
            
            <CheckboxLabel>
              <Checkbox
                type="checkbox"
                name="taxSoftware"
                value="Lacerte"
                checked={formData.taxSoftware.includes('Lacerte')}
                onChange={handleCheckboxChange}
              />
              Lacerte
            </CheckboxLabel>
            
            <CheckboxLabel>
              <Checkbox
                type="checkbox"
                name="taxSoftware"
                value="Drake"
                checked={formData.taxSoftware.includes('Drake')}
                onChange={handleCheckboxChange}
              />
              Drake
            </CheckboxLabel>
            
            <CheckboxLabel>
              <Checkbox
                type="checkbox"
                name="taxSoftware"
                value="ProSeries"
                checked={formData.taxSoftware.includes('ProSeries')}
                onChange={handleCheckboxChange}
              />
              ProSeries
            </CheckboxLabel>
            
            <CheckboxLabel>
              <Checkbox
                type="checkbox"
                name="taxSoftware"
                value="Other"
                checked={formData.taxSoftware.includes('Other')}
                onChange={handleCheckboxChange}
              />
              Other
            </CheckboxLabel>
          </CheckboxGroup>
          {errors.taxSoftware && <ErrorMessage>{errors.taxSoftware}</ErrorMessage>}
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="numberOfReturns">Approximate Number of Returns Prepared Annually</Label>
          <Select
            id="numberOfReturns"
            name="numberOfReturns"
            value={formData.numberOfReturns}
            onChange={handleInputChange}
          >
            <option value="">Select range</option>
            <option value="<100">Less than 100</option>
            <option value="100-250">100-250</option>
            <option value="251-500">251-500</option>
            <option value="501-1000">501-1000</option>
            <option value="1000+">More than 1000</option>
          </Select>
          {errors.numberOfReturns && <ErrorMessage>{errors.numberOfReturns}</ErrorMessage>}
        </FormGroup>
      </>
    );
  };

  const renderPaymentForm = () => {
    return (
      <>
        <PricingInfo>
          <PriceTitle>Early Access Pricing</PriceTitle>
          <Price>$15/user/month</Price>
          <PriceDescription>
            Join our waitlist today and be among the first to access FylePrep when we launch.
            <strong>Your card will only be charged when we grant you access to the platform.</strong>
            Adding payment details is optional - you can still join the waitlist without them.
          </PriceDescription>
        </PricingInfo>
      
        <FormGroup>
          <Label htmlFor="cardName">Name on Card</Label>
          <Input
            type="text"
            id="cardName"
            name="cardName"
            value={formData.cardName}
            onChange={handleInputChange}
          />
          {errors.cardName && <ErrorMessage>{errors.cardName}</ErrorMessage>}
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="cardNumber">Card Number</Label>
          <Input
            type="text"
            id="cardNumber"
            name="cardNumber"
            placeholder="1234 5678 9012 3456"
            value={formData.cardNumber}
            onChange={handleInputChange}
            maxLength={19}
          />
          {errors.cardNumber && <ErrorMessage>{errors.cardNumber}</ErrorMessage>}
        </FormGroup>
        
        <CardRow>
          <FormGroup>
            <Label htmlFor="cardExpiry">Expiry Date</Label>
            <Input
              type="text"
              id="cardExpiry"
              name="cardExpiry"
              placeholder="MM/YY"
              value={formData.cardExpiry}
              onChange={handleInputChange}
              maxLength={5}
            />
            {errors.cardExpiry && <ErrorMessage>{errors.cardExpiry}</ErrorMessage>}
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="cardCvc">CVC</Label>
            <Input
              type="text"
              id="cardCvc"
              name="cardCvc"
              placeholder="123"
              value={formData.cardCvc}
              onChange={handleInputChange}
              maxLength={4}
            />
            {errors.cardCvc && <ErrorMessage>{errors.cardCvc}</ErrorMessage>}
          </FormGroup>
        </CardRow>
      </>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderContactForm();
      case 2:
        return renderBusinessForm();
      case 3:
        return renderPaymentForm();
      default:
        return null;
    }
  };

  const renderSuccess = () => {
    return (
      <SuccessContainer
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <SuccessIcon>✅</SuccessIcon>
        <SuccessTitle>You're on the waitlist!</SuccessTitle>
        <SuccessMessage>
          Thank you for joining the FylePrep waitlist. We've sent a confirmation email to your inbox.
          Please check your email for details about next steps. We'll notify you as soon as we're ready to grant you access.
          In the meantime, we'll keep you updated on our progress.
        </SuccessMessage>
        <Button as="a" href="/">Return to Home</Button>
      </SuccessContainer>
    );
  };

  return (
    <WaitlistContainer>
      <AnimatedBackground />
      <PageTitle
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        Join the FylePrep Waitlist
      </PageTitle>
      
      <PageSubtitle
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        Be among the first to streamline your tax preparation workflow with FylePrep.
        Early access members receive priority support and special pricing.
      </PageSubtitle>
      
      {!isSubmitted ? (
        <FormContainer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {renderStepIndicator()}
          
          <form onSubmit={handleSubmit}>
            {renderCurrentStep()}
            
            <ButtonGroup>
              {currentStep > 1 && (
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handlePrevious}
                  disabled={isSubmitting}
                >
                  Previous
                </Button>
              )}
              
              {currentStep < steps.length ? (
                <Button 
                  type="button" 
                  onClick={handleNext}
                  disabled={isSubmitting}
                  style={{ marginLeft: currentStep === 1 ? 'auto' : '0' }}
                >
                  Next
                </Button>
              ) : (
                <>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleSkipPayment(e)}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Skip Payment & Join Waitlist'}
                  </Button>
                  <Button 
                    type="submit" 
                    variant="primary"
                    disabled={isSubmitting}
                    style={{ marginLeft: '1rem' }}
                  >
                    {isSubmitting ? 'Submitting...' : 'Join Waitlist with Payment'}
                  </Button>
                </>
              )}
            </ButtonGroup>
            {submissionError && <ErrorMessage>{submissionError}</ErrorMessage>}
          </form>
        </FormContainer>
      ) : (
        renderSuccess()
      )}
    </WaitlistContainer>
  );
};

export default WaitlistPage;
